// Used only in mobile view

import { Button, Dialog, DialogActions, DialogTitle, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { useState } from 'react'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import { ActiveBackgroundNavBarItem, AnimatedLabel, IconContainer } from 'components/NavBar'
import AutoGraphIcon from '@mui/icons-material/AutoGraph'
import { OrganizationKind, SubscriptionStatus, User } from 'api/model'
import { useAuthToken } from 'hooks/use-auth-token'
import { useActiveOrganization } from 'hooks/use-organization'
import { useRecoilValue } from 'recoil'
import styled from '@emotion/styled'
import { getColorForDarkMode } from 'utils'
import { useLocation, useNavigate } from 'react-router-dom'
import { ReactComponent as AppIcon } from 'components/NavBar/app-icon-dark.svg'
import { ReactComponent as JelledTextDark } from '../NavBar/jelled-text-dark.svg'
import { ReactComponent as JelledTextLight } from '../NavBar/jelled-text-light.svg'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined'
import { authenticatedUserAtom, organizationFamily } from 'state'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import Groups3Icon from '@mui/icons-material/Groups3'
import Groups3OutlinedIcon from '@mui/icons-material/Groups3Outlined'
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { COLORS } from 'app-constants'
import { useUpdateKeyContext } from 'hooks/use-context-key'

export const accessiblePathsForCancelledSubscription = ['/organization', '/organization/billing']

export const FeedbackBadgeStack = styled(Stack)`
  margin-right: 8px;
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  border-radius: 12px;
  justify-content: center;
  align-items: center;
  background-color: red;
`

type MenuItem = {
  name: string
  path: string
}

const NestedNavMenu = ({
  onBackClick,
  pathname,
  search,
  onMenuItemClick,
}: {
  onBackClick: () => void
  pathname: string
  search: string
  onMenuItemClick: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const organization = useRecoilValue(organizationFamily(activeOrganization ?? ''))
  const organizationKind = organization?.organization_kind ?? 'personal'
  const navigate = useNavigate()
  const user = useRecoilValue(authenticatedUserAtom)

  let backButtonText = ''
  let menuItems: MenuItem[] = []
  switch (pathname) {
    case '/emails':
    case '/emails?currentPage=0':
    case '/gmail': // TODO: this is deprecated
    case '/gmail?currentPage=0':
      break
    case '/jelledbots':
    case '/jelledbots?isReviews=true':
      menuItems = [
        {
          name: 'Conversations',
          path: '/jelledbots',
        },
        {
          name: 'Reviews',
          path: '/jelledbots?isReviews=true',
        },
      ]
      backButtonText = 'Jelledbots'
      break
    case '/my-twin':
    case '/my-twin/notes':
    case '/my-twin/sharing':
    case '/my-twin/emails':
    case '/my-twin/calendars':
    case '/my-twin/files':
    case '/my-twin/instructions':
      menuItems = [
        {
          name: 'Description',
          path: '/my-twin',
        },
        {
          name: 'Sharing',
          path: '/my-twin/sharing',
        },
        {
          name: 'Notes',
          path: '/my-twin/notes',
        },
        {
          name: 'Emails',
          path: '/my-twin/emails',
        },
        {
          name: 'Calendars',
          path: '/my-twin/calendars',
        },
        {
          name: 'Files',
          path: '/my-twin/files',
        },
        {
          name: 'Instructions',
          path: '/my-twin/instructions',
        },
      ]
      backButtonText = 'My Twin'
      break
    case '/organization':
    case '/organization/emails':
    case '/organization/personal-space':
    case '/organization/company':
      menuItems =
        organizationKind === OrganizationKind.company
          ? [
              {
                name: 'Account',
                path: '/organization',
              },
              { name: 'Company', path: '/organization/company' },
              { name: 'Emails', path: '/organization/emails' },
              { name: 'Billing', path: '/organization/billing' },
            ]
          : [
              { name: 'Account', path: '/organization' },
              { name: 'Personal space', path: '/organization/personal-space' },
              { name: 'Emails', path: '/organization/emails' },
              { name: 'Billing', path: '/organization/billing' },
            ]
      backButtonText = 'Settings'
      break
    case '/admin':
    case '/admin/accounts':
    case '/admin/metrics':
    case '/admin/ai-metrics':
    case '/admin/twin-metrics':
    case '/admin/prompts':
      menuItems = [
        {
          name: 'Customer account access',
          path: '/admin',
        },
        {
          name: 'Account management',
          path: '/admin/accounts',
        },
        { name: 'App metrics', path: '/admin/metrics' },
        { name: 'AI metrics', path: '/admin/ai-metrics' },
        { name: 'Twin metrics', path: '/admin/twin-metrics' },
        { name: 'Prompts', path: '/admin/prompts' },
      ]
      backButtonText = 'Admin'
      break
  }

  // filter menu items based on whether or not the subscription is cancelled

  const filteredMenuItems = menuItems.filter((menuItem) => {
    if (user?.subscription?.status === SubscriptionStatus.canceled) {
      return accessiblePathsForCancelledSubscription.includes(menuItem.path)
    }
    return true
  })

  return (
    <Stack spacing={2}>
      <Button
        variant="text"
        onClick={onBackClick}
        startIcon={
          <ArrowBackIosIcon
            sx={{
              fill: COLORS.GRAY_3,
              mr: '4px',
            }}
          />
        }
        sx={{
          fontSize: '16px',
          fontWeight: 500,
          color: COLORS.GRAY_3,
          width: 'fit-content',
        }}>
        {backButtonText}
      </Button>
      <Stack spacing={2}>
        {filteredMenuItems.map((menuItem) => {
          const { name, path } = menuItem
          return (
            <Stack
              key={name}
              onClick={() => {
                navigate(path)
                onMenuItemClick()
              }}
              sx={{
                width: '100%',
                p: 1,
                fontSize: '14px',
                fontWeight: 400,
                color: COLORS.GRAY_3,
                justifyContent: 'flex-start',
                borderRadius: '8px',
              }}>
              {name}
            </Stack>
          )
        })}
      </Stack>
    </Stack>
  )
}

const NavMenu = ({ user }: { user: User }) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
  const [, setAuthToken] = useAuthToken()
  const [, setActiveOrganization] = useActiveOrganization()
  const updateKey = useUpdateKeyContext()

  const isSubscriptionCancelled = user?.subscription?.status === SubscriptionStatus.canceled

  const onLogoutClicked = () => {
    setIsLogoutDialogOpen(true)
  }

  const onLogoutConfimed = () => {
    setAuthToken(null)
    setActiveOrganization(null)
    // clear <RecoilRoot /> by passing in an updated key
    updateKey()

    // clear session storage
    sessionStorage.clear()

    setIsLogoutDialogOpen(false)
  }

  // used to render the additional menu items for the my twin route
  const location = useLocation()
  const currentRoute = location.pathname

  const navigate = useNavigate()

  const [isViewingNestedMenu, setIsViewingNestedMenu] = useState(false)
  const onBackClick = () => {
    setIsViewingNestedMenu(false)
  }
  const onMenuItemClick = () => {
    setIsViewingNestedMenu(false)
    setIsMenuOpen(false)
  }

  // if menu is open and we are in nested level, render the nested level
  const renderMenuContent = () => {
    if (isViewingNestedMenu) {
      return (
        <NestedNavMenu
          onBackClick={onBackClick}
          pathname={currentRoute}
          search={location.search}
          onMenuItemClick={onMenuItemClick}
        />
      )
    }

    return (
      <Stack spacing={2}>
        {!isSubscriptionCancelled && (
          <ActiveBackgroundNavBarItem
            to="/"
            onClick={() => {
              setIsMenuOpen(false)
            }}>
            <IconContainer>
              {window.location.pathname === '/' ? <TipsAndUpdatesIcon /> : <TipsAndUpdatesOutlinedIcon />}
            </IconContainer>

            <AnimatedLabel
              isExpanded={isMenuOpen}
              variant={window.location.pathname === '/' ? 'h6' : 'body0'}
              sx={{
                color: window.location.pathname === '/' ? COLORS.JELLED_LIGHT_BLUE : COLORS.GRAY_3,
              }}>
              Insights
            </AnimatedLabel>
          </ActiveBackgroundNavBarItem>
        )}

        {!isSubscriptionCancelled && (
          <ActiveBackgroundNavBarItem
            to="/trends"
            onClick={() => {
              setIsMenuOpen(false)
            }}>
            <IconContainer>
              <AutoGraphIcon />
            </IconContainer>

            <AnimatedLabel
              isExpanded={isMenuOpen}
              variant={window.location.pathname === '/trends' ? 'h6' : 'body0'}
              sx={{
                color: window.location.pathname === '/trends' ? COLORS.JELLED_LIGHT_BLUE : COLORS.GRAY_3,
              }}>
              Trends
            </AnimatedLabel>
          </ActiveBackgroundNavBarItem>
        )}

        {!isSubscriptionCancelled && (
          <ActiveBackgroundNavBarItem
            to="/emails"
            onClick={() => {
              setIsMenuOpen(false)
            }}>
            <IconContainer>
              <EmailOutlinedIcon
                style={{
                  width: '24px',
                  height: '24px',
                }}
              />
            </IconContainer>

            <AnimatedLabel
              isExpanded={true}
              sx={{
                width: '100%',
              }}>
              <Typography
                component="span"
                display="inline-block"
                variant={window.location.pathname === '/emails' ? 'h6' : 'body0'}>
                Emails
              </Typography>
            </AnimatedLabel>
          </ActiveBackgroundNavBarItem>
        )}

        {!isSubscriptionCancelled && (
          <ActiveBackgroundNavBarItem
            to="/jelledbots"
            onClick={() => {
              setIsViewingNestedMenu(true)
            }}>
            <IconContainer>
              {window.location.pathname === '/jelledbots' ? <Groups3Icon /> : <Groups3OutlinedIcon />}
            </IconContainer>

            <AnimatedLabel
              isExpanded={true}
              variant={window.location.pathname === '/jelledbots' ? 'h6' : 'body0'}
              sx={{
                width: '100%',
              }}>
              JelledBots
            </AnimatedLabel>
            <ArrowForwardIosIcon
              sx={{
                fill: COLORS.GRAY_3,
                width: '16px',
                height: '16px',
                mr: 1,
              }}
            />
          </ActiveBackgroundNavBarItem>
        )}

        <Typography
          component="span"
          variant="pXS"
          sx={{
            color: COLORS.GRAY_4,
          }}>
          CONFIGURATION
        </Typography>

        {!isSubscriptionCancelled && (
          <ActiveBackgroundNavBarItem
            to={'/my-twin'}
            onClick={() => {
              setIsViewingNestedMenu(true)
            }}>
            <IconContainer>
              <SupportAgentOutlinedIcon />
            </IconContainer>

            <AnimatedLabel
              isExpanded={true}
              variant={window.location.pathname === '/my-twin' ? 'h6' : 'body0'}
              sx={{
                width: '100%',
              }}>
              My Twin
            </AnimatedLabel>
            <ArrowForwardIosIcon
              sx={{
                fill: COLORS.GRAY_3,
                width: '16px',
                height: '16px',
                mr: 1,
              }}
            />
          </ActiveBackgroundNavBarItem>
        )}

        <ActiveBackgroundNavBarItem
          to="/organization"
          onClick={() => {
            setIsViewingNestedMenu(true)
          }}>
          <IconContainer>
            {window.location.pathname === '/organization' ? <SettingsIcon /> : <SettingsOutlinedIcon />}
          </IconContainer>

          <AnimatedLabel
            isExpanded={true}
            variant={window.location.pathname === '/organization' ? 'h6' : 'body0'}
            sx={{
              width: '100%',
            }}>
            Settings
          </AnimatedLabel>

          <ArrowForwardIosIcon
            sx={{
              fill: COLORS.GRAY_3,
              width: '16px',
              height: '16px',
              mr: 1,
            }}
          />
        </ActiveBackgroundNavBarItem>

        {!isSubscriptionCancelled && (
          <ActiveBackgroundNavBarItem
            to="/admin"
            onClick={() => {
              setIsViewingNestedMenu(true)
            }}>
            <IconContainer>
              {window.location.pathname === '/admin' ? <AdminPanelSettingsIcon /> : <AdminPanelSettingsOutlinedIcon />}
            </IconContainer>

            <AnimatedLabel
              isExpanded={true}
              variant={window.location.pathname === '/admin' ? 'h6' : 'body0'}
              sx={{
                width: '100%',
              }}>
              Admin
            </AnimatedLabel>

            <ArrowForwardIosIcon
              sx={{
                fill: COLORS.GRAY_3,
                width: '16px',
                height: '16px',
                mr: 1,
              }}
            />
          </ActiveBackgroundNavBarItem>
        )}

        <Button
          onClick={() => {
            setIsMenuOpen(false)
            onLogoutClicked()
          }}
          style={{
            color: COLORS.GRAY_3,
            alignSelf: 'flex-start',
            paddingLeft: '12px',
            fontSize: '14px',
            fontWeight: 500,
            marginTop: '32px',
          }}>
          Log out
        </Button>
      </Stack>
    )
  }

  return (
    <>
      {isMenuOpen ? (
        <Stack
          position="fixed"
          sx={{
            backgroundColor: COLORS.PURE_WHITE,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: '100vh',
            width: '100vw',
            zIndex: 9999,
            display: 'block',
            paddingTop: '72px',
            paddingLeft: '16px',
            paddingRight: '16px',
            // add animation slide in from left to the container stack when menu is open and slide left when menu is closed
            animation: `${isMenuOpen ? 'slideInFromLeft 0.3s ease-in' : 'slideOutToLeft 0.3s ease-out'}`,
            '@keyframes slideInFromLeft': {
              from: { transform: 'translateX(-100%)' },
              to: { transform: 'translateX(0)' },
            },
            '@keyframes slideOutToLeft': {
              from: { transform: 'translateX(0)' },
              to: { transform: 'translateX(-100%)' },
            },
          }}>
          <Stack mb={4} direction="row" alignItems="center" justifyContent="space-between">
            <Stack
              direction="row"
              alignItems="center"
              spacing="8px"
              onClick={() => {
                setIsViewingNestedMenu(false)
                navigate('/')
              }}
              style={{
                cursor: 'pointer',
              }}>
              <AppIcon />
              {theme.palette.mode === 'light' ? (
                <JelledTextDark
                  style={{
                    width: '72px',
                    height: '30px',
                  }}
                />
              ) : (
                <JelledTextLight
                  style={{
                    width: '72px',
                    height: '30px',
                  }}
                />
              )}
            </Stack>
            <IconButton onClick={() => setIsMenuOpen(false)}>
              <CloseIcon
                style={{
                  fill: COLORS.GRAY_2,
                }}
              />
            </IconButton>
          </Stack>
          {renderMenuContent()}
        </Stack>
      ) : (
        <IconButton
          onClick={() => setIsMenuOpen(true)}
          style={{
            position: 'absolute',
            left: '16px',
            paddingLeft: 0,
          }}>
          <MenuIcon />
        </IconButton>
      )}
      <Dialog
        open={isLogoutDialogOpen}
        sx={{
          width: isMobile ? 'calc(100% - 32px)' : '640px',
        }}>
        <DialogTitle>Are you sure you want to logout?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setIsLogoutDialogOpen(false)
            }}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={onLogoutConfimed}>
            Yes, logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default NavMenu
